/**
 * Mini-cart drawer: AJAX add-to-cart intercept and drawer UI.
 *
 * Handles:
 * - AJAX add-to-cart for single product pages (form.cart intercept)
 * - Drawer open/close with slide animation
 * - Close via button, backdrop click, and Escape key
 * - added_to_cart event listener (fires for both single product and archive AJAX)
 * - removed_from_cart event listener (closes drawer if cart becomes empty)
 *
 * @package Sansegynge
 */
(function($) {
    'use strict';

    // DOM refs (set on DOMContentLoaded)
    let drawer, backdrop, panel, closeBtn;
    let isOpen = false;

    // --- Drawer open/close ---

    /**
     * Open the mini-cart drawer with slide-in animation.
     *
     * Removes invisible/pointer-events-none, toggles opacity on backdrop,
     * removes translate-x-full on panel, locks body scroll, and focuses
     * close button for keyboard accessibility.
     */
    function openDrawer() {
        if (!drawer) return;
        isOpen = true;

        // Make drawer container interactive
        drawer.classList.remove('invisible', 'pointer-events-none');

        // Fade in backdrop
        backdrop.classList.remove('opacity-0');
        backdrop.classList.add('opacity-100');

        // Slide panel in
        panel.classList.remove('translate-x-full');
        panel.classList.add('translate-x-0');

        // Accessibility
        drawer.setAttribute('aria-hidden', 'false');

        // Lock body scroll
        document.body.style.overflow = 'hidden';

        // Focus close button for keyboard users
        closeBtn.focus();
    }

    /**
     * Close the mini-cart drawer with slide-out animation.
     *
     * Slides panel out, fades backdrop, unlocks body scroll,
     * and after 300ms transition completes, adds invisible/pointer-events-none.
     * Uses setTimeout instead of transitionend (unreliable with multiple transitions).
     */
    function closeDrawer() {
        if (!drawer) return;
        isOpen = false;

        // Slide panel out
        panel.classList.remove('translate-x-0');
        panel.classList.add('translate-x-full');

        // Fade out backdrop
        backdrop.classList.remove('opacity-100');
        backdrop.classList.add('opacity-0');

        // Accessibility
        drawer.setAttribute('aria-hidden', 'true');

        // Unlock body scroll
        document.body.style.overflow = '';

        // After transition completes (300ms matches duration-300), hide container
        setTimeout(function() {
            if (!isOpen) {
                drawer.classList.add('invisible', 'pointer-events-none');
            }
        }, 300);
    }

    // --- AJAX add-to-cart for single product pages ---

    /**
     * Intercept form.cart submit events on single product pages.
     *
     * Serializes the form data and posts to WC's add_to_cart AJAX endpoint.
     * For variable products, validates that a variation is selected first.
     * On success, triggers the standard added_to_cart event which refreshes
     * all cart fragments (including our drawer content).
     */
    function initSingleProductAjax() {
        $(document).on('submit', 'form.cart', function(e) {
            e.preventDefault();
            var $form = $(this);
            var $button = $form.find('button[type="submit"], input[type="submit"]');

            // For variable products, check that a variation is selected
            var variationId = 0;
            if ($form.hasClass('variations_form') || $form.find('input[name="variation_id"]').length) {
                variationId = parseInt($form.find('input[name="variation_id"]').val(), 10);
                if (!variationId || variationId === 0) {
                    // Let WC handle the error (show "please select options")
                    return;
                }
            }

            $button.addClass('loading').prop('disabled', true);

            // Build AJAX data. WC_AJAX::add_to_cart() reads product_id and
            // determines whether the product is a variation or simple product.
            // For variable products, we must send the variation_id as
            // product_id so WC resolves the parent + variation correctly.
            // Sending the parent product ID causes WC to fail with
            // "Please choose product options" because variation_id stays 0.
            var productId = variationId || $button.val() || $form.find('[name="add-to-cart"]').val();

            var data = {
                product_id: productId,
                quantity: $form.find('input[name="quantity"]').val() || 1
            };

            // Include variation attributes for completeness.
            if (variationId) {
                $form.find('.variations select').each(function() {
                    data[$(this).attr('name')] = $(this).val();
                });
            }

            $.ajax({
                type: 'POST',
                url: wc_add_to_cart_params.wc_ajax_url.toString().replace('%%endpoint%%', 'add_to_cart'),
                data: data,
                dataType: 'json',
                success: function(response) {
                    if (response.error && response.product_url) {
                        window.location = response.product_url;
                        return;
                    }
                    // Trigger WC's standard event -- refreshes all fragments including our drawer
                    $(document.body).trigger('added_to_cart', [response.fragments, response.cart_hash, $button]);
                },
                error: function() {
                    // On error, fall back to standard form submission
                    $form.off('submit').submit();
                },
                complete: function() {
                    $button.removeClass('loading').prop('disabled', false);
                }
            });
        });
    }

    // --- dataLayer ---

    /**
     * Push add_to_cart_stape from the fragment SG_Cart_Events attaches to the
     * add-to-cart response. The GTM container listens for exactly this event
     * name (custom event trigger "ce - add_to_cart_stape").
     *
     * Silent when the fragment is missing (refreshed fragments, a failed add),
     * so nothing is pushed twice and nothing is pushed for non-adds.
     */
    function pushAddToCartEvent(fragments) {
        var key = 'script#sg-added-to-cart';
        if (!fragments || typeof fragments[key] !== 'string') {
            return;
        }
        var match = fragments[key].match(/<script[^>]*>([\s\S]*?)<\/script>/);
        if (!match) {
            return;
        }
        var payload;
        try {
            payload = JSON.parse(match[1]);
        } catch (err) {
            return;
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(payload);
    }

    // --- Event listeners ---

    /**
     * Bind all drawer interaction events.
     *
     * - Close button click
     * - Backdrop click
     * - Escape key
     * - added_to_cart (fires for both single product AJAX and archive page WC AJAX)
     * - removed_from_cart (closes drawer if cart is now empty)
     */
    function bindEvents() {
        // Close button click
        closeBtn.addEventListener('click', closeDrawer);

        // Backdrop click
        backdrop.addEventListener('click', closeDrawer);

        // Escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && isOpen) {
                closeDrawer();
            }
        });

        // added_to_cart event (fires for BOTH single product AJAX AND archive page AJAX)
        $(document.body).on('added_to_cart', function(e, fragments, cart_hash, $button) {
            pushAddToCartEvent(fragments);
            openDrawer();
        });

        // removed_from_cart event (fires when remove button clicked in drawer)
        $(document.body).on('removed_from_cart', function(e, fragments, cart_hash, $button) {
            // Fragments already replaced by WC. If cart is now empty, close the drawer.
            if ($('.sg-mini-cart-empty').length) {
                closeDrawer();
            }
        });
    }

    // --- Init ---

    $(document).ready(function() {
        drawer = document.querySelector('[data-mini-cart-drawer]');
        backdrop = document.querySelector('[data-mini-cart-backdrop]');
        panel = document.querySelector('[data-mini-cart-panel]');
        closeBtn = document.querySelector('[data-mini-cart-close]');

        if (!drawer || !panel || !closeBtn || !backdrop) {
            console.warn('[Sansegynge] Mini-cart drawer elements not found.');
            return;
        }

        initSingleProductAjax();
        bindEvents();

        console.log('[Sansegynge] Mini-cart drawer initialized.');
    });

})(jQuery);
